import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SiweMessage } from 'siwe';
import { User } from '@/types';
import { api } from '@/services/api';

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;
  login: (address: string, chainId: number) => Promise<void>;
  logout: () => void;
  checkAuth: (address: string) => Promise<boolean>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      token: null,

      login: async (address: string, chainId: number) => {
        try {
          set({ isLoading: true, error: null });

          // In development or when API is unavailable, use demo login
          if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
            console.log('Using demo login in development environment');
            
            // Import demo user
            const { demoUser } = await import('@/services/demoData');
            
            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Set demo user and token
            set({
              token: 'demo-token-' + Math.random().toString(36).substring(2),
              user: {
                id: demoUser.id,
                walletAddress: address || demoUser.walletAddress,
                username: demoUser.username,
                email: demoUser.email,
                avatarUrl: demoUser.profileImage,
                isCreator: demoUser.isCreator,
                isAdmin: demoUser.isAdmin,
              },
              isAuthenticated: true,
              isLoading: false,
            });
            
            return;
          }

          try {
            // Step 1: Get a nonce from the server
            const nonceResponse = await api.post('/auth/nonce', { walletAddress: address });
            // Extract nonce from response, handle different response formats
            const responseData = nonceResponse.data as any;
            const nonce = responseData?.nonce || (responseData?.data as any)?.nonce || '';

            // Step 2: Create and sign the SIWE message
            const domain = window.location.host;
            const origin = window.location.origin;
            
            const message = new SiweMessage({
              domain,
              address,
              statement: 'Sign in to FTTF.World',
              uri: origin,
              version: '1',
              chainId,
              nonce,
            });
            
            const messageToSign = message.prepareMessage();
            
            // Request signature from wallet
            const signature = await window.ethereum.request({
              method: 'personal_sign',
              params: [messageToSign, address],
            });

            // Step 3: Verify the signature with the server
            const verifyResponse = await api.post('/auth/verify', {
              message: messageToSign,
              signature,
            });

            // Extract token and user from response, handle different response formats
            const verifyData = verifyResponse.data as any;
            const token = verifyData?.token || (verifyData?.data as any)?.token || null;
            const user = verifyData?.user || (verifyData?.data as any)?.user || null;

            // Step 4: Store the token and user data
            set({
              token,
              user,
              isAuthenticated: true,
              isLoading: false,
            });

            // Set the token in the API service
            api.setToken(token);
          } catch (apiError) {
            console.error('API login error, falling back to demo login:', apiError);
            
            // Import demo user for fallback
            const { demoUser } = await import('@/services/demoData');
            
            // Set demo user and token as fallback
            set({
              token: 'demo-token-' + Math.random().toString(36).substring(2),
              user: {
                id: demoUser.id,
                walletAddress: address || demoUser.walletAddress,
                username: demoUser.username,
                email: demoUser.email,
                avatarUrl: demoUser.profileImage,
                isCreator: demoUser.isCreator,
                isAdmin: demoUser.isAdmin,
              },
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('Login error:', error);
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Unknown error during login' 
          });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
        
        // Clear the token in the API service
        api.setToken(null);
      },

      checkAuth: async (address: string) => {
        const { token, user } = get();
        
        // If we have a token and the wallet address matches, we're authenticated
        if (token && user && user.walletAddress.toLowerCase() === address.toLowerCase()) {
          return true;
        }
        
        // Otherwise, we need to log in again
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
        
        return false;
      },
    }),
    {
      name: 'fttf-auth-storage',
      partialize: (state) => ({ token: state.token, user: state.user }),
    }
  )
);